/**
 * Public Provider Profile Page
 *
 * Displays a content provider's public profile with banner, logo, bio,
 * social links, and their published titles in a rail format.
 * Design mirrors Title.tsx with a hero backdrop and content rails.
 */

/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */

import type { ApiPublicProviderProfile } from "@lumi/data";
import { Button } from "@lumi/ui-headless";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, Globe } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { useParams } from "react-router-dom";
import { apiCall } from "../../config/api";
import { LoadingSpinner } from "../../shared/components/loading/LoadingSpinner";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../../shared/transitions/pageTransitions";
import { CollectionRail } from "../home/components/CollectionRail";

// Social platform icons (inline SVG for simplicity)
const SocialIcons = {
  twitter: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  instagram: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  youtube: (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z"
        clipRule="evenodd"
      />
    </svg>
  ),
  vimeo: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197c1.185-1.044 2.351-2.084 3.501-3.128C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.493 4.797l-.013.01z" />
    </svg>
  ),
};

interface SocialLinkButtonProps {
  readonly platform: keyof typeof SocialIcons;
  readonly url: string;
}

function SocialLinkButton({ platform, url }: SocialLinkButtonProps): ReactElement {
  const handleClick = (): void => {
    if (globalThis.electronAPI) {
      globalThis.electronAPI.openExternal(url);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex items-center justify-center w-10 h-10 rounded-full bg-none text-neutral-300 hover:text-lumi-accent transition-colors cursor-pointer"
      aria-label={`Visit ${platform}`}
    >
      {SocialIcons[platform]}
    </button>
  );
}

/**
 * Public Provider Profile Page
 */
export function ProviderProfilePage(): ReactElement {
  // NOSONAR
  const { slug } = useParams<{ slug: string }>();
  const [provider, setProvider] = useState<ApiPublicProviderProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isTransitioning, transitionTo, supportsViewTransitions } = useTransitionNavigation();

  useEffect(() => {
    async function loadProvider(): Promise<void> {
      if (!slug) {
        setError("No provider specified");
        setLoading(false);
        return;
      }

      try {
        const response = await apiCall(`/v1/providers/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError("Provider not found");
          } else {
            throw new Error(`API Error: ${response.status}`);
          }
          setLoading(false);
          return;
        }

        const data = (await response.json()) as ApiPublicProviderProfile;
        setProvider(data);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load provider:", err);
        setError("Failed to load provider profile");
        setLoading(false);
      }
    }

    void loadProvider();
  }, [slug]);

  const handleBack = (): void => {
    transitionTo("/home", pageTransitions.fullScreenFade);
  };

  // Error state
  if (error || (!provider && !loading)) {
    return (
      <div className="bg-transparent min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error || "Provider not found"}</p>
          <Button onClick={handleBack} variant="ghost">
            <ArrowLeft size={18} className="mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (!provider) {
    return (
      <motion.div
        className="bg-transparent min-h-screen flex items-center justify-center"
        {...(supportsViewTransitions
          ? {}
          : {
              initial: "initial",
              animate: "animate",
              variants: pageTransitions.cinematicFade,
            })}
      >
        <LoadingSpinner centered />
      </motion.div>
    );
  }

  // Build social links array for rendering
  const socialLinks: { platform: keyof typeof SocialIcons; url: string }[] = [];
  if (provider.socialLinks?.twitter) socialLinks.push({ platform: "twitter", url: provider.socialLinks.twitter });
  if (provider.socialLinks?.instagram) socialLinks.push({ platform: "instagram", url: provider.socialLinks.instagram });
  if (provider.socialLinks?.youtube) socialLinks.push({ platform: "youtube", url: provider.socialLinks.youtube });
  if (provider.socialLinks?.vimeo) socialLinks.push({ platform: "vimeo", url: provider.socialLinks.vimeo });

  return (
    <motion.div
      className="bg-transparent min-h-screen flex flex-col overflow-hidden content-rails"
      {...(supportsViewTransitions
        ? {}
        : {
            initial: "initial",
            animate: isTransitioning ? "exit" : "animate",
            exit: "exit",
            variants: pageTransitions.cinematicFade,
          })}
    >
      {/* Back Button */}
      <div className="absolute top-16 md:top-[72px] lg:top-20 right-3 md:right-4 lg:left-8 z-50 pointer-events-auto">
        <Button onClick={handleBack} variant="ghost-sm" aria-label="Go back">
          <ArrowLeft size={18} className="md:w-5 md:h-5" />
        </Button>
      </div>

      {/* Backdrop Image - Full screen with layered gradients (matches Title.tsx) */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: provider.bannerUrl ? `url(${provider.bannerUrl})` : undefined,
            backgroundColor: provider.bannerUrl ? undefined : "#1a1a1a",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-black/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
        {/* Extended gradient for smooth content transition */}
        <div className="absolute bottom-0 left-0 right-0 h-[600px] bg-gradient-to-t from-black via-black/35 to-transparent" />
      </div>

      {/* Profile Content - Positioned at bottom like Title.tsx */}
      <div className="absolute bottom-0 left-0 right-0 flex flex-col md:flex-row md:items-end md:gap-8">
        {/* Hero Section - Provider Profile Info */}
        <section className="relative z-10 px-4 md:px-8 lg:px-16 pb-6 md:pb-8 md:flex-1">
          <div className="max-w-2xl lg:max-w-3xl pb-2">
            {/* Logo or Name (matches TitleHero pattern) */}
            {provider.logoUrl ? (
              <img
                src={provider.logoUrl}
                alt={`${provider.displayName} logo`}
                className="h-28 md:h-36 lg:h-48 w-auto max-w-full object-contain pb-4 sm:pb-2 md:pb-4 mr-auto"
              />
            ) : (
              <div className="flex items-center gap-3 pb-4 md:pb-8">
                <h1 className="text-5xl md:text-6xl lg:text-8xl font-bold leading-tight">{provider.displayName}</h1>
                {provider.verified && (
                  <BadgeCheck
                    className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 text-lumi-accent flex-shrink-0"
                    aria-label="Verified provider"
                  />
                )}
              </div>
            )}

            {/* Links Row */}
            <div className="flex flex-wrap items-center gap-3 pb-3 sm:pb-4">
              {provider.websiteUrl && (
                <button
                  type="button"
                  onClick={() => {
                    const url = provider.websiteUrl;
                    if (!url) return;

                    if (globalThis.electronAPI) {
                      globalThis.electronAPI.openExternal(url);
                    } else {
                      window.open(url, "_blank", "noopener,noreferrer");
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-0 bg-none text-neutral-300 hover:text-lumi-accent transition-all cursor-pointer"
                >
                  <Globe className="w-5 h-5" />
                </button>
              )}

              {socialLinks.map((link) => (
                <SocialLinkButton key={link.platform} platform={link.platform} url={link.url} />
              ))}
            </div>

            {/* Bio */}
            {provider.bio && (
              <p className="text-sm md:text-sm text-neutral-200 pb-4 sm:pb-6 leading-relaxed whitespace-pre-wrap max-w-2xl">
                {provider.bio}
              </p>
            )}
          </div>
        </section>

        {/* Titles Content Rail - Right half only with horizontal cards */}
        {provider.titles.length > 0 && (
          <section className="relative pointer-events-auto z-20 w-full md:w-1/2 md:ml-auto pb-8 sm:pb-12">
            {/* Gradient mask to fade rail into left half */}
            <div className="absolute inset-y-0 border-l border-s-neutral-800 border-e-neutral-800/0 left-0 w-32 pointer-events-none z-10 hidden md:block" />
            <CollectionRail
              title=""
              items={provider.titles}
              cardType="HORIZONTAL"
              loading={false}
              transitionTo={transitionTo}
              hideActions
              preferInfoOnEnter
              hideDetails
            />
          </section>
        )}

        {/* Empty state for titles */}
        {provider.titles.length === 0 && (
          <section className="relative pointer-events-auto z-20 w-full md:w-1/2 md:ml-auto pb-8">
            <div className="text-center py-12 text-neutral-400 italic">
              <p>No published titles yet</p>
            </div>
          </section>
        )}

        {/* Visual overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/0 pointer-events-none" />
      </div>
    </motion.div>
  );
}
